// src/features/infoscreens/components/InfoscreensPanel.jsx
// Infoscreen list, shown as a tab on the Settings page.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Paper,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Tv as TvIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  MeetingRoom as MeetingRoomIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';

// Hooks
import { useInfoscreens } from '../hooks/useInfoscreens';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { usePermissions } from '../../auth/hooks/usePermissions';

// Utils
import { UUIDDisplay } from '../../../utils/formatters/uuid';

export default function InfoscreensPanel() {
  const { t } = useTranslation(['infoscreens', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const permissions = usePermissions();

  // State
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedInfoscreen, setSelectedInfoscreen] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // API hooks
  const {
    infoscreens,
    loading,
    error,
    clearError,
    fetchInfoscreens,
    deleteInfoscreen,
  } = useInfoscreens();

  // Load initial data
  useEffect(() => {
    fetchInfoscreens();
  }, [fetchInfoscreens]);

  // Event handlers
  const handleRefresh = useCallback(() => {
    fetchInfoscreens();
  }, [fetchInfoscreens]);

  const handleCreateClick = useCallback(() => {
    navigate('/infoscreens/create');
  }, [navigate]);

  const handleViewInfoscreen = useCallback((infoscreen) => {
    navigate(`/infoscreens/${infoscreen.infoscreen_id}`);
  }, [navigate]);

  const handleEditInfoscreen = useCallback((infoscreen) => {
    navigate(`/infoscreens/${infoscreen.infoscreen_id}/edit`);
  }, [navigate]);

  const handleMenuOpen = useCallback((event, infoscreen) => {
    event.stopPropagation();
    setSelectedInfoscreen(infoscreen);
    setActionMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setSelectedInfoscreen(null);
  }, []);

  const handleDeleteInfoscreen = useCallback((infoscreen) => {
    showConfirmDialog({
      title: t('infoscreens:confirm.delete_infoscreen_title'),
      message: t('infoscreens:confirm.delete_infoscreen_message', { name: infoscreen.name }),
      confirmText: t('infoscreens:confirm.delete_confirm'),
      cancelText: t('common:buttons.cancel'),
      severity: 'error',
      onConfirm: async () => {
        try {
          const result = await deleteInfoscreen(infoscreen.infoscreen_id);
          if (result.success) {
            showSuccess(t('infoscreens:success.infoscreen_deleted'));
          } else {
            showError(result.error || t('infoscreens:errors.delete_failed'));
          }
        } catch (error) {
          showError(t('infoscreens:errors.delete_failed'));
        }
      },
    });
    handleMenuClose();
  }, [showConfirmDialog, deleteInfoscreen, showSuccess, showError, handleMenuClose, t]);

  // Check permissions
  const canCreate = permissions.hasScope('infoscreen:create');
  const canUpdate = permissions.hasScope('infoscreen:update');
  const canDelete = permissions.hasScope('infoscreen:delete');

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: t('infoscreens:grid.columns.name'),
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={2} sx={{ py: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <TvIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('common:labels.id')} <UUIDDisplay uuid={params.row.infoscreen_id} />
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'login_code',
      headerName: t('infoscreens:grid.columns.login_code'),
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'rooms',
      headerName: t('infoscreens:grid.columns.assigned_rooms'),
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <MeetingRoomIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {params.value?.length || 0}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: t('infoscreens:grid.columns.created'),
      width: 140,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {params.value ? <LocalizedDateDistance date={params.value} /> : t('infoscreens:grid.columns.unknown')}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: t('infoscreens:grid.columns.actions'),
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ], [handleMenuOpen, t]);

  return (
    <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('infoscreens:subtitle')}
      </Typography>

      <Box mb={2} display="flex" gap={1}>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            disabled={loading}
          >
            {t('infoscreens:actions.add_infoscreen')}
          </Button>
        )}
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={loading}>
          {t('common:buttons.refresh')}
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* DataGrid */}
      <Box
        sx={{
          minHeight: 400,
          width: '100%',
          '& .MuiDataGrid-root': {
            border: 'none',
            '& .MuiDataGrid-main': {
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                '&:focus': {
                  outline: 'none',
                },
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            },
          },
        }}
      >
        <DataGrid
          rows={infoscreens}
          columns={columns}
          getRowId={(row) => row.infoscreen_id}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          onRowClick={(params) => handleViewInfoscreen(params.row)}
          rowHeight={72}
          getRowHeight={() => 'auto'}
          slots={{
            noRowsOverlay: () => (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                py={4}
              >
                <TvIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('infoscreens:messages.no_infoscreens')}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" paragraph>
                  {t('infoscreens:messages.no_infoscreens_description')}
                </Typography>
                {canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateClick}
                  >
                    {t('infoscreens:messages.create_first_infoscreen')}
                  </Button>
                )}
              </Box>
            ),
          }}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
        }}
      >
        <MenuItem onClick={() => { handleViewInfoscreen(selectedInfoscreen); handleMenuClose(); }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('infoscreens:actions.view_details')}</ListItemText>
        </MenuItem>

        {canUpdate && (
          <MenuItem onClick={() => { handleEditInfoscreen(selectedInfoscreen); handleMenuClose(); }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('infoscreens:actions.edit')}</ListItemText>
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem onClick={() => { handleDeleteInfoscreen(selectedInfoscreen); handleMenuClose(); }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              <Typography color="error">{t('infoscreens:actions.delete')}</Typography>
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Confirm Dialog */}
      <ConfirmDialog />
    </Paper>
  );
}